import React, { memo, useState } from 'react';
import AppHeader from '../../common/app-header/AppHeader';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import styles from './DocumentDetails.module.scss';
import { Box, Button, Grid, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import AISummaryDetails from './components/ai-summary-details/AISummaryDetails';
import DocumentContent from './components/document-content/DocumentContent';
import DocumentDetailsBlock from './components/document-details/DocumentDetailsBlock';
import { useDocumentItem } from './hooks/useDocumentItem';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../../common/constants/routes';
import { sanitizeRedirectUrl } from '../../common/helpers/sanitize';
import { useDocumentDetails } from './hooks/useDocumentDetails';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { useDocumentHTML } from './hooks/useDocumentHTML';
import { useDocumentSigning } from '../document-vault/hooks/useDocumentSigning';
import MessageModal from '../../common/message-modal/MessageModal';
import FileTypeDownloadModal from '../../common/file-type-download-modal/FileTypeDownloadModal';
import { useDeleteMessageModal } from '../document-vault/hooks/useDeleteMessageModal';
import { useFileTypeDownload } from '../../common/file-type-download-modal/useFileTypeDownload';
import { useTemplateCreation } from './hooks/useTemplateCreation';
import { useLatestVersion } from './hooks/useLatestVersion';
import BrushIcon from '../../../assets/svg/brush.svg?react';
import { useDocumentsFunctions } from '../document-vault/hooks/useDocumentsFunctions';
import Checkbox from '@mui/material/Checkbox';
import { selectUserRoleByGroupName } from '../../slices/user/userSlice';
import { GROUPS } from '../../common/constants/groups';
import { AssignProfilePersonModal } from '../../common/assign-profile-person-modal/AssignProfilePersonModal';
import { showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';
import { useAssignProfilePersonModal } from '../../common/assign-profile-person-modal/useAssignProfilePersonModal';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { selectAllEmployees } from '../../slices/employees/employeesSlice';
import { selectAllCustomers } from '../../slices/customers/customersSlice';
import {
  BusinessProfileContacts,
  selectBusinessProfileContacts,
} from '../../slices/business-profile/businessProfileSlice';
import { selectAllSuppliers } from '../../slices/suppliers/suppliersSlice';
import { Employee } from '../../models/employee.model';
import { Customer } from '../../models/customer.model';
import { Supplier } from '../../models/supplier.model';
import { Helpers } from '../../common/helpers/helpers';
import { useDocumentContent } from './hooks/useDocumentContent';
import { PacmanLoaderBlock } from '../../common/pacman-loader';
import ProgressBarFancy from '../../common/fancy-progress-bar';
import { clearPlaceholdersIds } from '../../slices/template-placeholders-ids/templatePlaceholdersIds';
import { clearPlaceholdersIndexes } from '../../slices/template-placeholders-indexes/templatePlaceholdersIndexes';
import { useDocumensoRedirect } from '../../common/hooks/useDocumensoRedirect';
import { Document } from '../document-vault/components/document-list-vault/types';
import { TemplatePlaceholdersBlock } from './components/document-content/components/TemplatePlaceholdersBlock';
import { resetTemplatePlaceholders } from '../../slices/template-placeholders/templatePlaceholdersSlice';
import { selectDocumentDetails } from '../../slices/document-details/documentDetailsSlice';
import { ActionButtons } from './components/action-buttons/ActionButtons';
import InfoIcon from '@mui/icons-material/Info';
import { useAISummary } from './hooks/useAISummary';
import { DocumentVaultItemModel } from '../../models/document.vault.item.model';

const DocumentDetails = () => {
  const { t } = useTranslation();
  const { activeTab, handleTabChange } = useDocumentItem();
  const { documentId } = useParams();
  const dispatch = useAppDispatch();
  const [params] = useSearchParams();
  const contractGenerated = params.get('contractGenerated');
  const generateTemplateParam = params.get('generateTemplate');
  const backURL = params.get('backURL');
  const location = useLocation();
  const path = location.pathname;
  const navigate = useNavigate();
  const { checkedLatestVersion } = useLatestVersion();
  const documentDetails = useAppSelector(selectDocumentDetails);
  const employeesStatus = useAppSelector((state) => state.employees.status);
  const suppliersStatus = useAppSelector((state) => state.suppliers.status);
  const customersStatus = useAppSelector((state) => state.customers.status);
  const businessContactsStatus = useAppSelector((state) => state.businessProfile.status);
  const userIsEmployee = Boolean(
    useAppSelector(selectUserRoleByGroupName(GROUPS.COMPANY_EMPLOYEE))
  );
  const { redirectToDocumenso } = useDocumensoRedirect();
  const detailsProps = {
    ...useDocumentDetails({
      id: Number(documentId),
      isDocumentContract: Boolean(contractGenerated),
      allowedCreatingHTML: activeTab === 0,
    }),
  };
  const { navigateToDocumentPageTab } = useDocumentsFunctions();
  const htmlArray = contractGenerated
    ? null
    : !detailsProps.documentUploadLatestHTMLId && detailsProps.documentDetails
      ? detailsProps.documentDetails?.document_upload_html
      : detailsProps.documentUploadLatestHTMLId
        ? [detailsProps.documentUploadLatestHTMLId]
        : [];
  const {
    documentHTML,
    setDocumentHTML,
    saveDocumentHTML,
    pendingStatus: pendingStatusHTML,
    contentEditable,
    setContentEditable,
    showConfirmationModal,
    setShowConfirmationModal,
  } = useDocumentHTML(
    htmlArray ?? undefined,
    activeTab === 0,
    detailsProps.documentDetails && 'contract_generation' in detailsProps.documentDetails
      ? detailsProps.documentDetails?.html
      : null
  );

  const dataFetching =
    detailsProps.loadingData ||
    suppliersStatus === 'loading' ||
    customersStatus === 'loading' ||
    employeesStatus === 'loading' ||
    businessContactsStatus === 'loading';

  const {
    document: documentToSign,
    setDocument: setDocumentToSign,
    handleSignOriginalDocument,
    handleSignLatestVersion,
    setOpenAssignPersonDialog,
    openAssignPersonDialog,
  } = useDocumentSigning();
  const { patchDocumentWithProfilePerson, setAssignee, assignee } = useAssignProfilePersonModal(
    documentToSign?.category,
    documentToSign
  );
  const {
    setModalOpen: setDeleteMessageModalOpen,
    modalOpen: deleteMessageModalOpen,
    pendingStatus: documentDeletePendingStatus,
    documentToDelete,
    setDocumentToDelete,
    handleDeleteDocument,
  } = useDeleteMessageModal();
  const {
    setModalOpen: setFileTypeModalOpen,
    modalOpen: fileTypeModalOpen,
    handleCloseModal: handleCloseFileTypeModal,
    setDocument: setDocumentForDownloading,
    document: documentForDownloading,
    setFileName: setFileNameForDownloading,
    fileName: fileNameForDownloading,
    fileType: fileTypeForDownloading,
    setFileType: setFileTypeForDownloading,
    handleDownloadContract,
    downloadDocumentUploadItem,
    handleDownloadHTML,
  } = useFileTypeDownload();
  const [pdfSaveDialogOpen, setPdfSaveDialogOpen] = useState(false);
  const {
    templateCreationEnabled,
    setTemplateCreationEnabled,
    pendingStatus: templateCreationPending,
    creatingTemplate,
    createTemplateInitial,
    setDocumentTemplate,
    deletePlaceholders,
    templateCreationStep,
    setTemplateCreationStep,
    setDeletePlaceholdersMessageVisible,
    deletePlaceholdersMessageVisible,
    templateName,
    setTemplateName,
    category,
    setCategory,
    documentTypeLabel,
    setDocumentTypeLabel,
    documentWorkflowLabel,
    setDocumentWorkflowLabel,
    customLabel,
    setCustomLabel,
    categorySelectValues,
    getTypeLabels,
    workflowsArray,
  } = useTemplateCreation(
    Number(documentId),
    Boolean(contractGenerated),
    contractGenerated
      ? undefined
      : detailsProps?.documentDetails &&
          (detailsProps?.documentDetails?.document_upload_html?.length ?? 0) > 0
        ? Math[checkedLatestVersion ? 'max' : 'min'](
            ...(detailsProps?.documentDetails?.document_upload_html ?? [])
          )
        : detailsProps?.documentUploadLatestHTMLId,
    detailsProps?.documentDetails?.name as string,
    detailsProps?.documentDetails as Document,
    documentHTML
  );
  const { fileBlobURL, fileLoading, pdfBlobMode, setPDFBlobMode } = useDocumentContent(
    detailsProps?.documentDetails?.id as number,
    detailsProps?.documentDetails && detailsProps?.documentDetails?.du_filetype
      ? detailsProps?.documentDetails?.du_filetype[0]
      : null
  );
  const { setAISummaryCreationMode, aiSummaryCreationMode } = useAISummary();
  const employees = useAppSelector(selectAllEmployees);
  const customers = useAppSelector(selectAllCustomers);
  const businessContacts = useAppSelector(selectBusinessProfileContacts);
  const suppliers = useAppSelector(selectAllSuppliers);

  const workflowsProperty = detailsProps?.documentDetails?.contract_generation
    ? 'dc_workflows'
    : 'du_workflows';

  const checkProfilePersonsAvailability = (category: string) => {
    switch (category) {
      case 'employment':
        return employees?.length > 0;
      case 'business':
        return false;
      case 'customers':
        return customers?.length > 0;
      case 'suppliers':
        return false;
    }
  };
  const documentFromProfileEntity = [
    ROUTES.SUPPLIER_PROFILES,
    ROUTES.EMPLOYEE_PROFILES,
    ROUTES.CUSTOMER_PROFILES,
    ROUTES.BUSINESS_PROFILE,
  ].some((el) => backURL?.includes(el));
  const documentFromKeyDates = [ROUTES.DOCUMENTS_KEY_DATES].some((el) => backURL?.includes(el));
  const categoryName = (category: string | undefined) => {
    switch (category) {
      case 'employment':
        return t('Employees');
      case 'customers':
        return t('Customers');
      case 'business':
        return t('Business');
      case 'suppliers':
        return t('Suppliers');
      default:
        return '';
    }
  };

  const determineProfileRoutes = (category: string | undefined) => {
    switch (category) {
      case 'employment':
        return ROUTES.EMPLOYEE_PROFILES;
      case 'customers':
        return ROUTES.CUSTOMER_PROFILES;
      case 'business':
        return ROUTES.BUSINESS_PROFILE;
      case 'suppliers':
        return ROUTES.SUPPLIER_PROFILES;
      default:
        return null;
    }
  };

  const profileLabelsProp = (category: string | undefined) => {
    if (category) {
      switch (category) {
        case 'employment':
          return 'employees';
        case 'suppliers':
          return 'suppliers';
        case 'customers':
          return 'customers';
        case 'business':
          return 'business_profile_contacts';
      }
    }
  };

  const profileNameProp = (category: string) => {
    if (category) {
      switch (category) {
        case 'employment':
          return 'full_name';
        case 'suppliers':
          return 'name';
        case 'customers':
          return 'customer_name';
        case 'business':
          return 'contact_name';
      }
    }
  };

  const profileItems = (
    category: string | undefined
  ): Employee[] | Supplier[] | Customer[] | BusinessProfileContacts[] | undefined => {
    switch (category) {
      case 'employment':
        return employees;
      case 'suppliers':
        return suppliers;
      case 'customers':
        return customers;
      case 'business':
        return businessContacts ?? [];
    }
  };

  const profileEntityName = () => {
    const category = documentDetails?.category;
    const item = category
      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        profileItems(category).find(
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          (el) => el.id === documentDetails[profileLabelsProp(category)][0]
        )
      : null;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return item ? item[profileNameProp(category)] : '';
  };

  const documentIsSubscription = () => {
    const typeProperty =
      detailsProps &&
      detailsProps.documentDetails &&
      'contract_generation' in detailsProps.documentDetails
        ? 'dc_types'
        : 'du_types';
    return (
      (detailsProps?.documentDetails?.[typeProperty]?.length ?? 0) > 0 &&
      detailsProps?.documentDetails?.[typeProperty]?.[0] === 'Subscription'
    );
  };

  return (
    <>
      <AppHeader
        title={
          path.includes(ROUTES.WORKSPACE) ? t('headers.workspace') : t('headers.documentVault')
        }
      />
      <Box sx={{ marginTop: '16px' }}>
        <Grid component='div' container className={styles.header}>
          <Grid
            component='div'
            sx={{
              display: { md: 'flex' },
              gap: '8px',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
            size={{ xs: 12 }}
          >
            <Box className={styles.breadcrumbs}>
              {backURL ? (
                documentFromProfileEntity ? (
                  <>
                    {!userIsEmployee && (
                      <>
                        <Link
                          id='documentDetailsBreadcrumbCategoryLink'
                          className={styles.documentViewText}
                          to={determineProfileRoutes(documentDetails?.category) ?? ''}
                        >
                          {documentDetails ? categoryName(documentDetails?.category) : ''}
                        </Link>
                        <Typography variant='body2' className={styles.separator}>
                          ›
                        </Typography>
                      </>
                    )}
                    {documentDetails?.category !== 'business' && (
                      <>
                        <Link
                          id='documentDetailsBreadcrumbProfileLink'
                          className={styles.documentViewText}
                          to={
                            documentDetails
                              ? `${determineProfileRoutes(documentDetails?.category)}/${
                                  documentDetails[profileLabelsProp(documentDetails?.category)!]
                                }`
                              : ''
                          }
                        >
                          {documentDetails &&
                            profileItems(documentDetails?.category) &&
                            profileEntityName()}
                        </Link>
                        <Typography variant='body2' className={styles.separator}>
                          ›
                        </Typography>
                      </>
                    )}
                    <Link
                      id='documentDetailsBreadcrumbWorkflowLink'
                      className={styles.documentViewText}
                      to={
                        documentDetails
                          ? `${determineProfileRoutes(documentDetails?.category)}/${
                              documentDetails?.category !== 'business'
                                ? documentDetails[profileLabelsProp(documentDetails?.category)!]
                                : ''
                            }?workflow=${
                              documentDetails[workflowsProperty] &&
                              documentDetails[workflowsProperty].length > 0
                                ? documentDetails[workflowsProperty][0]
                                : t('headers.general')
                            }`
                          : ''
                      }
                    >
                      {documentDetails
                        ? documentDetails[workflowsProperty] &&
                          documentDetails[workflowsProperty].length > 0
                          ? documentDetails[workflowsProperty][0]
                          : t('labels.generalAdministration')
                        : ''}
                    </Link>
                    <Typography variant='body2' className={styles.separator}>
                      ›
                    </Typography>
                    <Typography variant='body2' className={styles.documentViewText}>
                      {documentDetails ? documentDetails.name : ''}
                    </Typography>
                  </>
                ) : documentFromKeyDates ? (
                  <>
                    <Link
                      id='documentDetailsBreadcrumbKeyDatesLink'
                      className={styles.documentViewText}
                      to={
                        ROUTES.DOCUMENTS_KEY_DATES +
                        `${documentIsSubscription() ? '/?activeTab=Subscriptions' : ''}`
                      }
                    >
                      {t('headers.keyDates')}
                    </Link>
                    <Typography variant='body2' className={styles.separator}>
                      ›
                    </Typography>
                    <Typography variant='body2' className={styles.documentViewText}>
                      {documentDetails ? documentDetails.name : ''}
                    </Typography>
                  </>
                ) : (
                  <Link
                    id='documentDetailsBackLink'
                    className={styles.documentViewText}
                    to={sanitizeRedirectUrl(backURL)}
                  >
                    {t('buttons.back')}
                  </Link>
                )
              ) : (
                <>
                  <Link
                    id='documentDetailsBreadcrumbVaultLink'
                    className={styles.documentViewText}
                    to={path.includes(ROUTES.WORKSPACE) ? ROUTES.WORKSPACE : ROUTES.DOCUMENT_VAULT}
                  >
                    {path.includes(ROUTES.WORKSPACE)
                      ? t('headers.workspace')
                      : t('headers.documentVault')}
                  </Link>
                  <Typography variant='body2' className={styles.separator}>
                    ›
                  </Typography>
                  <Link
                    id='documentDetailsBreadcrumbVaultCategoryLink'
                    className={styles.documentViewText}
                    to={
                      path.includes(ROUTES.WORKSPACE)
                        ? ROUTES.WORKSPACE + `?activeTab=${documentDetails?.category}`
                        : ROUTES.DOCUMENT_VAULT + `?activeTab=${documentDetails?.category}`
                    }
                  >
                    {documentDetails
                      ? Helpers.capitalizeFirstLetter(documentDetails?.category)
                      : ''}
                  </Link>
                  <Typography variant='body2' className={styles.separator}>
                    ›
                  </Typography>
                  <Typography variant='body2' className={styles.documentViewText}>
                    {documentDetails ? documentDetails.name : ''}
                  </Typography>
                </>
              )}
            </Box>
            <Box sx={{ marginTop: { xs: '16px', md: 0 } }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center' }}
                role='toolbar'
                aria-label={t('labels.documentActions')}
              >
                {detailsProps.documentDetails?.is_flattened && (
                  <Tooltip title={t('messages.flattenedPDFMessage')} arrow placement='top'>
                    <InfoIcon
                      color='primary'
                      fontSize='small'
                      style={{ marginRight: '8px' }}
                      className={'info-icon'}
                    />
                  </Tooltip>
                )}
                {detailsProps.documentDetails &&
                  detailsProps.documentDetails?.signing_doc_id !== null &&
                  detailsProps.documentDetails?.signing_doc_id !== undefined &&
                  !detailsProps.isImage &&
                  !detailsProps.documentDetails?.is_flattened && (
                    <Button
                      id='documentContentPendingSigningBtn'
                      type='button'
                      className={
                        'btn-default btn-action-basic btn-small btn-green' +
                        (detailsProps.documentDetails?.status === 'PENDING' ? ' btn-blue' : '')
                      }
                      role='button'
                      variant='contained'
                      onClick={() =>
                        redirectToDocumenso(
                          `${ROUTES.DOCUMENTS}/${detailsProps.documentDetails?.signing_doc_id}?view=true&backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}`
                        )
                      }
                      style={{ marginRight: '8px' }}
                    >
                      {detailsProps.documentDetails?.status === 'COMPLETED'
                        ? t('buttons.signedDocument')
                        : t('buttons.pendingSigning')}
                    </Button>
                  )}
                {(detailsProps.documentDetails?.signing_doc_id === null ||
                  detailsProps.documentDetails?.signing_doc_id === undefined) && (
                  <ActionButtons
                    isImage={detailsProps.isImage}
                    handleSaveDocument={saveDocumentHTML}
                    handleAISummaryGeneration={() => {
                      if (activeTab === 0) {
                        handleTabChange(null, 1);
                        navigate(
                          location.pathname +
                            `?initialTab=1&generateSummary=true${
                              'contract_generation' in
                              (detailsProps?.documentDetails as DocumentVaultItemModel)
                                ? '&contractGenerated=true'
                                : ''
                            }`
                        );
                      } else {
                        setAISummaryCreationMode(true);
                      }
                    }}
                    handleDocumentDownload={() => {
                      if (
                        detailsProps.documentDetails &&
                        'contract_generation' in detailsProps.documentDetails
                      ) {
                        setDocumentForDownloading(detailsProps.documentDetails);
                        setFileNameForDownloading(detailsProps.documentDetails?.name);
                        setFileTypeModalOpen(true);
                      } else {
                        /*if (
                          (detailsProps.documentDetails?.document_upload_html?.length ?? 0) > 0 ||
                          detailsProps.documentUploadLatestHTMLId
                        ) {
                          setDocumentForDownloading(detailsProps.documentDetails);
                          setFileTypeModalOpen(true);
                        } else {*/
                        downloadDocumentUploadItem(
                          detailsProps?.documentDetails?.id as number,
                          detailsProps?.documentDetails?.name as string
                        );
                        /*}*/
                      }
                    }}
                    handleSignDocument={
                      detailsProps.documentDetails &&
                      detailsProps.documentDetails?.signing_doc_id !== null &&
                      detailsProps.documentDetails?.signing_doc_id !== undefined
                        ? undefined
                        : () => {
                            setDocumentToSign(
                              detailsProps.documentDetails as DocumentVaultItemModel
                            );
                            if (
                              detailsProps.documentDetails?.signing_doc_id !== null &&
                              detailsProps.documentDetails?.signing_doc_id !== undefined
                            ) {
                              redirectToDocumenso(
                                `${ROUTES.DOCUMENTS}/${detailsProps.documentDetails?.signing_doc_id}?view=true&backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}`
                              );
                            } else {
                              if (
                                documentDetails?.customers.length === 0 &&
                                documentDetails?.employees.length === 0 &&
                                documentDetails?.category !== 'business' &&
                                documentDetails?.category !== 'suppliers' &&
                                checkProfilePersonsAvailability(documentDetails?.category)
                              ) {
                                setDocumentToSign(documentDetails);
                                setOpenAssignPersonDialog(true);
                              } else {
                                dispatch(showOverlay());
                                const url = `${window.location.origin}${ROUTES.PROCESSING}`;
                                const tab = window.open('', '_blank');
                                // @ts-expect-error: there can be an error
                                tab.location.href = url;
                                handleSignOriginalDocument(
                                  documentDetails as Document,
                                  undefined,
                                  undefined,
                                  // @ts-expect-error: there can be an error
                                  tab
                                );
                              }
                            }
                          }
                    }
                    setEditingDocument={setContentEditable}
                    handleEditDocument={() => {
                      if (!detailsProps.documentDetails?.is_flattened) {
                        setContentEditable(true);
                        setPDFBlobMode(false);
                      }
                    }}
                    handleCreateTemplate={undefined}
                    handleDeleteDocument={() => {
                      setDocumentToDelete(detailsProps.documentDetails);
                      setDeleteMessageModalOpen(true);
                    }}
                    buttonSaveDisabled={!contentEditable || pdfBlobMode || templateCreationEnabled}
                    handleCreateTemplateMainBtn={true}
                    creatingTemplateEnabled={templateCreationEnabled}
                    doc={detailsProps.documentDetails}
                    isDocumentContentTab={true}
                    cancelEditMode={() => {
                      setContentEditable(false);
                      setPDFBlobMode(false);
                    }}
                  />
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Grid
          component='div'
          container
          spacing={2}
          className='inputs-editable'
          style={{ justifyContent: 'space-between' }}
        >
          {activeTab === 0 ? (
            <Grid component='div' size={{ xs: 12 }} className={styles.gridItem}>
              <Tabs
                variant='scrollable'
                scrollButtons='auto'
                allowScrollButtonsMobile
                aria-label={t('labels.documentTabs')}
                value={activeTab}
                onChange={handleTabChange}
                className='tabs-document-details'
              >
                <Tab
                  id='documentDetailsDocumentContentTab'
                  aria-label={t('tabs.documentContent')}
                  label={t('tabs.documentContent')}
                  className={styles.tab}
                />
                <Tab
                  id='documentDetailsDocumentDetailsTab'
                  aria-label={t('tabs.documentDetails')}
                  label={t('tabs.documentDetails')}
                  className={styles.tab}
                />
              </Tabs>
              <Box className={styles.tabContent}>
                <DocumentContent
                  documentHTML={documentHTML}
                  setDocumentHTML={setDocumentHTML}
                  isImage={detailsProps.isImage}
                  contentEditable={contentEditable}
                  pdfBlobMode={pdfBlobMode}
                  setPDFBlobMode={setPDFBlobMode}
                  fileBlobURL={fileBlobURL}
                  fileLoading={fileLoading}
                  doc={detailsProps.documentDetails}
                  templateCreationEnabled={templateCreationEnabled}
                  setContentEditable={setContentEditable}
                  showConfirmationModal={showConfirmationModal}
                  setShowConfirmationModal={setShowConfirmationModal}
                  documentHTMLId={
                    !contractGenerated &&
                    detailsProps &&
                    detailsProps.documentDetails &&
                    detailsProps.documentDetails?.document_upload_html
                      ? Math[checkedLatestVersion ? 'max' : 'min'](
                          // eslint-disable-next-line no-unsafe-optional-chaining
                          ...detailsProps?.documentDetails?.document_upload_html
                        )
                      : undefined
                  }
                >
                  <TemplatePlaceholdersBlock
                    deletePlaceholdersMessageVisible={deletePlaceholdersMessageVisible}
                    setDeletePlaceholdersMessageVisible={setDeletePlaceholdersMessageVisible}
                    templateCreationStep={templateCreationStep}
                    setTemplateCreationStep={setTemplateCreationStep}
                    createTemplate={() => creatingTemplate()}
                    createTemplateInitial={() => createTemplateInitial()}
                    goBack={() => {
                      setTemplateCreationEnabled(false);
                      setDocumentTemplate(null);
                      dispatch(resetTemplatePlaceholders());
                      dispatch(clearPlaceholdersIds());
                      dispatch(clearPlaceholdersIndexes());
                      if (generateTemplateParam && detailsProps) {
                        navigateToDocumentPageTab(
                          detailsProps.documentDetails as DocumentVaultItemModel,
                          0
                        );
                      }
                    }}
                    documentHTMLId={
                      !contractGenerated &&
                      detailsProps &&
                      detailsProps.documentDetails &&
                      detailsProps.documentDetails?.document_upload_html
                        ? Math[checkedLatestVersion ? 'max' : 'min'](
                            // eslint-disable-next-line no-unsafe-optional-chaining
                            ...detailsProps?.documentDetails?.document_upload_html
                          )
                        : undefined
                    }
                    deletePlaceholders={deletePlaceholders}
                    typeLabels={getTypeLabels() ?? []}
                    workflowsArray={workflowsArray()}
                    categoryArray={categorySelectValues ?? []}
                    setCategory={setCategory}
                    setDocumentTypeLabel={setDocumentTypeLabel}
                    setDocumentWorkflowLabel={setDocumentWorkflowLabel}
                    setCustomLabel={setCustomLabel}
                    category={category}
                    documentTypeLabel={documentTypeLabel}
                    documentWorkflowLabel={documentWorkflowLabel}
                    templateName={templateName}
                    setTemplateName={setTemplateName}
                    customLabel={customLabel}
                  />
                </DocumentContent>
              </Box>
              {(pendingStatusHTML || detailsProps.loadingData) && !templateCreationPending && (
                <ProgressOverlay position='absolute' />
              )}
              {templateCreationPending &&
                !detailsProps.pdfConversionMessage &&
                !detailsProps.pendingStatus &&
                !pendingStatusHTML &&
                !dataFetching &&
                !detailsProps.loadingData && (
                  <PacmanLoaderBlock loading={templateCreationPending} />
                )}
            </Grid>
          ) : (
            <>
              <Grid
                component='div'
                size={{ xs: 12, lg: 5 }}
                className={`${styles.gridItem} ${
                  detailsProps.propertiesEditMode ? styles.card_border : ''
                }`}
                sx={{ position: 'relative' }}
              >
                <Box
                  style={{
                    display: 'flex',
                    paddingBottom: '8px',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '8px',
                    //flexWrap: 'wrap',
                  }}
                >
                  <Tabs
                    variant='standard'
                    scrollButtons='auto'
                    allowScrollButtonsMobile
                    value={activeTab}
                    onChange={handleTabChange}
                    className='tabs-document-details'
                  >
                    <Tab
                      id='documentDetailsDocumentContentTab2'
                      label={t('tabs.documentContent')}
                      className={styles.tab}
                    />
                    <Tab
                      id='documentDetailsDocumentDetailsTab2'
                      label={t('tabs.documentDetails')}
                      className={styles.tab}
                    />
                  </Tabs>
                  {detailsProps.propertiesEditMode ? (
                    <Box
                      style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        id='documentDetailsCancelBtn'
                        type='button'
                        className='btn-default btn-action-basic btn-cancel btn-small'
                        role='button'
                        variant='contained'
                        size='large'
                        onClick={() => {
                          detailsProps.setPropertiesEditMode(false);
                          detailsProps.setDetailsUpdated(false);
                          //detailsProps.computeAssigneeLabel();
                        }}
                      >
                        {t('buttons.cancel')}
                      </Button>
                      <Button
                        id='documentDetailsSaveBtn'
                        type='button'
                        className='btn-default btn-action-basic btn-small'
                        role='button'
                        variant='contained'
                        onClick={() => {
                          detailsProps.updateDocumentItem();
                          detailsProps.setPropertiesEditMode(false);
                          detailsProps.setDetailsUpdated(true);
                        }}
                      >
                        {t('buttons.save')}
                      </Button>
                    </Box>
                  ) : (
                    <>
                      {(documentDetails?.signing_doc_id === null ||
                        documentDetails?.signing_doc_id === undefined) && (
                        <Tooltip
                          title={
                            userIsEmployee ? t('messages.contactAdministrator') : t('buttons.edit')
                          }
                          placement='top'
                          arrow
                        >
                          <IconButton
                            id='documentDetailsEditIconBtn'
                            size='small'
                            className={styles.editButton}
                            disabled={userIsEmployee}
                            onClick={
                              userIsEmployee
                                ? undefined
                                : () => detailsProps.setPropertiesEditMode(true)
                            }
                          >
                            <BrushIcon id='documentDetailsEditIconBtn' />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                </Box>
                <Box
                  className={styles.tabContent}
                  sx={{
                    height: { lg: `calc(100vh - 202px)` },
                    overflow: dataFetching ? 'hidden' : 'auto',
                  }}
                >
                  <DocumentDetailsBlock
                    {...detailsProps}
                    loadingData={dataFetching}
                    setCategory={detailsProps.setCategory}
                    profileLabelsArray={detailsProps.profileLabelsArray(detailsProps.category)}
                    workflowsArray={detailsProps.workflowsArray()}
                    categoryArray={detailsProps.categorySelectValues}
                    typeLabels={detailsProps.getTypeLabels()}
                  />
                </Box>
                {dataFetching && <ProgressOverlay position='absolute' />}
              </Grid>
              <Grid component='div' size={{ xs: 12, lg: 7 }}>
                <Box
                  sx={{ height: { lg: `calc(100vh - 140px)` } }}
                  className={styles.gridItemBoxShadow}
                >
                  <AISummaryDetails
                    isImage={detailsProps.isImage}
                    aiSummaryCreationMode={aiSummaryCreationMode}
                    setAISummaryCreationMode={setAISummaryCreationMode}
                    doc={detailsProps.documentDetails}
                    pendingStatus={detailsProps.loadingData}
                    allowGettingAISummary={detailsProps ? detailsProps.htmlRetrieved : false}
                  />
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
      {/*signingDialogOpen && (
        <MessageModal
          open={signingDialogOpen}
          modalMessage={t('messages.signingVersionMessage')}
          actionButtonText={t('buttons.signLatestVersion')}
          actionButtonFn={() =>
            handleSignLatestVersion(documentToSign, detailsProps?.documentUploadLatestHTMLId)
          }
          actionButtonAdditionalText={t('buttons.signOriginalDocument')}
          actionButtonAdditionalFn={() => handleSignOriginalDocument(documentToSign)}
          goBackButtonText={t('buttons.goBack')}
          document={documentToSign}
          onClose={() => {
            setDocumentToSign(null);
            setSigningDialogOpen(false);
          }}
        />
      )*/}
      {openAssignPersonDialog && documentToSign && (
        <AssignProfilePersonModal
          open={openAssignPersonDialog}
          onClose={() => {
            setOpenAssignPersonDialog(false);
            setDocumentToSign(null as unknown as Document);
            setAssignee('');
          }}
          modalTitle={t('headers.startSigning')}
          category={documentToSign?.category}
          document={documentToSign}
          goBackButtonText={t('buttons.goBack')}
          modalMessage={t('messages.signingRecommendations')}
          assignee={assignee}
          setAssignee={setAssignee}
          secondaryAction={() => {
            dispatch(showOverlay());
            const url = `${window.location.origin}${ROUTES.PROCESSING}`;
            const tab = window.open('', '_blank');
            // @ts-expect-error: there can be an error
            tab.location.href = url;
            if (assignee && assignee !== 'noAssignee') {
              patchDocumentWithProfilePerson()
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                .then((res) => handleSignOriginalDocument(res, undefined, undefined, tab))
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                .catch(() => handleSignOriginalDocument(documentToSign, undefined, undefined, tab));
            } else {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              handleSignOriginalDocument(documentToSign, undefined, undefined, tab);
            }
          }}
          primaryAction={() => {
            dispatch(showOverlay());
            const url = `${window.location.origin}${ROUTES.PROCESSING}`;
            const tab = window.open('', '_blank');
            // @ts-expect-error: there can be an error
            tab.location.href = url;
            if (assignee && assignee !== 'noAssignee') {
              patchDocumentWithProfilePerson()
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                .then((res) => handleSignLatestVersion(res, undefined, undefined, undefined, tab))
                .catch(() =>
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  handleSignLatestVersion(documentToSign, undefined, undefined, undefined, tab)
                );
            } else {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              handleSignLatestVersion(documentToSign, undefined, undefined, undefined, tab);
            }
          }}
          signOriginalDocument={pdfBlobMode}
        />
      )}
      {detailsProps.pdfConversionMessage && (contentEditable || templateCreationEnabled) && (
        <MessageModal
          open={detailsProps.pdfConversionMessage}
          modalContent={
            <Box sx={{ width: '100%' }}>
              <p>{t('messages.pdfConversionMessagePart1')}</p>
              <p>{t('messages.pdfConversionMessagePart2')}</p>
              <Box
                className='checkbox-row'
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  rowGap: '8px',
                }}
              >
                <Checkbox
                  style={{ marginLeft: -10, paddingTop: 0, paddingBottom: 0 }}
                  id='dontShowPDFConversionMessage'
                  checked={detailsProps.dontShowPDFConversionMessage}
                  onChange={(event) => {
                    detailsProps.setDontShowPDFConversionMessage(event.target.checked);
                    localStorage.setItem(
                      'showPDFConversionMessage',
                      (!event.target.checked).toString()
                    );
                  }}
                />
                <label
                  id='documentDetailsDontShowPdfConversionMessageLabel'
                  style={{ marginTop: 0 }}
                  htmlFor='dontShowPDFConversionMessage'
                >
                  {t('labels.dontShowThisMessageAgain')}
                </label>
              </Box>
            </Box>
          }
          noActionButton={true}
          goBackButtonText={t('buttons.continue')}
          onClose={() => {
            detailsProps.setPDFConversionMessage(false);
            if (
              detailsProps?.documentDetails?.document_upload_html?.length === 0 &&
              !detailsProps?.documentUploadLatestHTMLId
            ) {
              detailsProps.setPDFDocHTMLCreationAllowed(true);
            }
          }}
        />
      )}
      {detailsProps.pdfDocHTMLCreationAllowed && (
        <MessageModal
          open={detailsProps.pdfDocHTMLCreationAllowed}
          modalMessage={t('messages.creatingHTMLForPDF')}
          noButtons={true}
          modalContent={
            <Box sx={{ width: '100%', padding: '20px 0 0' }}>
              <ProgressBarFancy initialValue={0} finalValue={100} duration={30000} />
            </Box>
          }
          onClose={() => {
            detailsProps.setPDFDocHTMLCreationAllowed(false);
          }}
        />
      )}
      {detailsProps.errorModalOpen && (
        <MessageModal
          open={detailsProps.errorModalOpen}
          modalMessage={t('messages.firstDocumentConversionFail')}
          actionButtonText={t('buttons.retry')}
          actionButtonFn={() => {
            detailsProps.setErrorModalOpen(false);
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            detailsProps.setErrorModalOpen(false);
          }}
        />
      )}
      {fileTypeModalOpen && documentForDownloading && (
        <FileTypeDownloadModal
          onClose={() => {
            handleCloseFileTypeModal();
          }}
          open={fileTypeModalOpen}
          fileName={fileNameForDownloading}
          fileType={fileTypeForDownloading}
          handleFileTypeChange={(value) =>
            setFileTypeForDownloading(value as React.SetStateAction<'pdf' | 'doc'>)
          }
          handleFileNameChange={setFileNameForDownloading}
          document={documentForDownloading}
          handleDownloadContract={() => {
            if (fileTypeForDownloading === 'pdf') {
              setPdfSaveDialogOpen(true);
              setFileTypeModalOpen(false);
            } else {
              handleDownloadContract(documentForDownloading.id);
            }
          }}
          handleDownloadDocument={() =>
            downloadDocumentUploadItem(documentForDownloading.id, documentForDownloading.name)
          }
          handleDownloadHTML={
            documentForDownloading?.du_filetype &&
            documentForDownloading?.du_filetype.includes('pdf')
              ? () => {
                  setPdfSaveDialogOpen(true);
                  setFileTypeModalOpen(false);
                }
              : () => {
                  handleDownloadHTML(
                    Array.isArray(documentForDownloading?.document_upload_html) &&
                      (documentForDownloading?.document_upload_html?.length ?? 0) > 0
                      ? Math.max(...documentForDownloading.document_upload_html)
                      : detailsProps?.documentUploadLatestHTMLId,
                    documentForDownloading.name.endsWith('.pdf') ||
                      documentForDownloading.name.endsWith('.docx') ||
                      documentForDownloading.name.endsWith('.doc')
                      ? documentForDownloading.name
                          .replace('.pdf', '')
                          .replace('.docx', '')
                          .replace('.doc', '')
                      : documentForDownloading.name,
                    documentForDownloading.du_filetype![0]
                  );
                }
          }
        />
      )}
      {pdfSaveDialogOpen && documentForDownloading && (
        <MessageModal
          open={pdfSaveDialogOpen}
          modalMessage={t('messages.chooseSaveAsPDF')}
          actionButtonText={t('buttons.ok')}
          actionButtonFn={() => {
            Helpers.convertHtmlToPdfAndDownload(
              documentHTML,
              documentForDownloading?.name.replace('.pdf', '')
            );
            setPdfSaveDialogOpen(false);
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            setPdfSaveDialogOpen(false);
          }}
        />
      )}
      {deleteMessageModalOpen && (
        <MessageModal
          open={deleteMessageModalOpen}
          modalTitle={t('dialogTitles.deleteDocument')}
          modalMessage={t('messages.deleteDocumentDialog')}
          actionButtonText={t('buttons.goBack')}
          actionButtonFn={() => {
            setDocumentToDelete(null);
            setDeleteMessageModalOpen(false);
          }}
          goBackButtonText={t('buttons.delete')}
          onDismiss={() => {
            setDocumentToDelete(null);
            setDeleteMessageModalOpen(false);
          }}
          document={documentToDelete ?? undefined}
          onClose={() => {
            handleDeleteDocument();
          }}
        />
      )}
      {(detailsProps.pendingStatus || documentDeletePendingStatus) && <ProgressOverlay />}
    </>
  );
};

export default memo(DocumentDetails);
